import { Form, FormQuestion, LandingPage } from '../types'
import { AVA_FORM, AVA_QUESTIONS } from '../data/avaForm'
import { AVA_LANDING } from '../data/avaLanding'
import { slugify, uid } from '../utils/format'
import { supabase } from './supabaseClient'
import {
  blockRow,
  formConfig,
  formRow,
  landingConfig,
  landingRow,
  questionRow,
} from './mappers'
import { saveConfig } from './store'

/** Nombre del formulario predeterminado de cada organización. */
export const DEFAULT_FORM_NAME = 'Solicitud de estrategia'

function newId(): string {
  return crypto.randomUUID ? crypto.randomUUID() : uid('id')
}

/**
 * `field_key` de cada pregunta de la plantilla: indica en qué columna de
 * `leads` vuelca su respuesta `submit_public_form`.
 */
const FIELD_KEYS: Record<string, string> = {
  q_ubicacion: 'city',
  q_instagram: 'instagram',
  q_descripcion: 'description',
  q_tipo_servicio: 'service_type',
  q_inversion: 'ad_investment',
  q_presupuesto: 'budget',
  q_comentarios: 'comments',
}

const CONTACT_FIELD_KEYS: Record<string, string> = {
  firstName: 'full_name',
  fullName: 'full_name',
  company: 'company',
  phone: 'phone',
  email: 'email',
}

/** Identifica una pregunta de la plantilla ya existente en la organización. */
function matches(row: { question?: string; field_key?: string }, template: FormQuestion) {
  const expectedKey = FIELD_KEYS[template.id]
  if (expectedKey && row.field_key === expectedKey) return true
  return (row.question ?? '').trim().toLowerCase() === template.label.trim().toLowerCase()
}

/**
 * Garantiza que la organización tiene el formulario predeterminado completo
 * (bloque de contacto + las seis preguntas) y al menos dos landings — con y
 * sin vídeo — asociadas a ese formulario.
 *
 * Es idempotente y no destructivo:
 *  - si el formulario no existe, lo crea entero;
 *  - si existe incompleto, añade SOLO las preguntas que falten al final;
 *  - conserva cualquier pregunta personalizada del usuario;
 *  - no duplica el bloque de contacto ni las landings.
 */
export async function ensureDefaultContent(
  organizationId: string,
  companyName: string,
): Promise<{ formId: string; created: boolean; addedQuestions: number }> {
  const base = slugify(companyName) || 'empresa'

  const { data: forms, error: formsError } = await supabase
    .from('forms')
    .select('*')
    .eq('organization_id', organizationId)
    .is('deleted_at', null)
    .order('created_at')
  if (formsError) {
    throw new Error(`No se han podido leer los formularios: ${formsError.message}`)
  }

  let formId = ''
  let created = false
  let addedQuestions = 0

  const existing =
    (forms ?? []).find(
      (f: { name?: string }) =>
        (f.name ?? '').trim().toLowerCase() === DEFAULT_FORM_NAME.toLowerCase(),
    ) ?? (forms ?? [])[0]

  if (!existing) {
    // No hay ningún formulario: se crea completo.
    const form: Form = {
      ...(JSON.parse(JSON.stringify(AVA_FORM)) as Form),
      id: newId(),
      organizationId,
      name: DEFAULT_FORM_NAME,
      slug: `${base}-solicitud-estrategia`,
      status: 'activo',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    formId = form.id
    created = true

    const { error } = await supabase.from('forms').insert(formRow(form))
    if (error) {
      throw new Error(`No se ha podido crear el formulario: ${error.message}`)
    }

    const rows = AVA_QUESTIONS.map((template, index) => {
      const question: FormQuestion = {
        ...template,
        id: newId(),
        formId: form.id,
        order: index + 1,
      }
      return {
        ...questionRow(question, form.id, organizationId),
        field_key: FIELD_KEYS[template.id] ?? null,
      }
    })
    const { error: questionsError } = await supabase
      .from('form_questions')
      .insert(rows)
    if (questionsError) {
      throw new Error(
        `No se han podido crear las preguntas: ${questionsError.message}`,
      )
    }
    addedQuestions = rows.length

    await saveConfig(organizationId, 'form', form.id, {
      ...formConfig(form),
      contactFieldKeys: CONTACT_FIELD_KEYS,
    })
  } else {
    formId = existing.id

    // Completa las preguntas que falten sin tocar las personalizadas.
    const { data: questionRows, error: readError } = await supabase
      .from('form_questions')
      .select('id, question, field_key, position')
      .eq('form_id', formId)
    if (readError) {
      throw new Error(`No se han podido leer las preguntas: ${readError.message}`)
    }

    const present = questionRows ?? []
    let nextPosition =
      present.reduce(
        (max: number, row: { position?: number }) => Math.max(max, row.position ?? 0),
        0,
      ) + 1

    const missing = AVA_QUESTIONS.filter(
      (template) => !present.some((row) => matches(row, template)),
    )
    if (missing.length) {
      const rows = missing.map((template) => {
        const question: FormQuestion = {
          ...template,
          id: newId(),
          formId,
          order: nextPosition++,
        }
        return {
          ...questionRow(question, formId, organizationId),
          field_key: FIELD_KEYS[template.id] ?? null,
        }
      })
      const { error: insertError } = await supabase
        .from('form_questions')
        .insert(rows)
      if (insertError) {
        throw new Error(
          `No se han podido añadir las preguntas que faltaban: ${insertError.message}`,
        )
      }
      addedQuestions = rows.length
    }

    // Asegura el bloque de contacto y el aviso de privacidad, sin duplicarlos.
    const { data: settings } = await supabase
      .from('organization_settings')
      .select('value')
      .eq('organization_id', organizationId)
      .eq('key', `form_config:${formId}`)
      .maybeSingle()
    const config = (settings?.value ?? {}) as Record<string, unknown>
    const contactFields = config.contactFields as unknown[] | undefined
    if (!contactFields || contactFields.length === 0) {
      await saveConfig(organizationId, 'form', formId, {
        ...config,
        contactFields: AVA_FORM.contactFields,
        contactPosition: 'inicio',
        contactTitle: AVA_FORM.contactTitle,
        contactFieldKeys: CONTACT_FIELD_KEYS,
        submitLabel: config.submitLabel ?? AVA_FORM.submitLabel,
        privacyNotice: config.privacyNotice ?? AVA_FORM.privacyNotice,
        status: config.status ?? 'activo',
      })
    }

    // El formulario predeterminado debe estar activo para poder publicarse.
    if (existing.is_active === false) {
      await supabase.from('forms').update({ is_active: true }).eq('id', formId)
    }
  }

  await ensureDefaultLandings(organizationId, companyName, formId, base)

  return { formId, created, addedQuestions }
}

/**
 * Crea (si faltan) la landing con vídeo y la landing sin vídeo, y asocia el
 * formulario real a todas las landings de la organización que no tengan uno.
 */
async function ensureDefaultLandings(
  organizationId: string,
  companyName: string,
  formId: string,
  base: string,
): Promise<void> {
  const { data: landings, error } = await supabase
    .from('landing_pages')
    .select('id, slug, form_id')
    .eq('organization_id', organizationId)
    .is('deleted_at', null)
  if (error) {
    throw new Error(`No se han podido leer las landings: ${error.message}`)
  }
  const rows = landings ?? []

  const withVideoSlug = base
  const withoutVideoSlug = `${base}-directa`

  if (!rows.some((l: { slug: string }) => l.slug === withVideoSlug)) {
    await createLandingFromTemplate(
      organizationId,
      formId,
      `Landing de ${companyName}`,
      withVideoSlug,
      true,
    )
  }
  if (!rows.some((l: { slug: string }) => l.slug === withoutVideoSlug)) {
    await createLandingFromTemplate(
      organizationId,
      formId,
      `Landing directa de ${companyName}`,
      withoutVideoSlug,
      false,
    )
  }

  // Landings existentes sin formulario asociado: se conectan al real.
  const orphans = rows
    .filter((l: { form_id: string | null }) => !l.form_id)
    .map((l: { id: string }) => l.id)
  if (orphans.length) {
    await supabase
      .from('landing_pages')
      .update({ form_id: formId })
      .in('id', orphans)
  }
}

/** Clona la plantilla de landing, con o sin bloque de vídeo. */
async function createLandingFromTemplate(
  organizationId: string,
  formId: string,
  name: string,
  slug: string,
  withVideo: boolean,
): Promise<void> {
  const landing: LandingPage = {
    ...(JSON.parse(JSON.stringify(AVA_LANDING)) as LandingPage),
    id: newId(),
    organizationId,
    formId,
    name,
    slug,
    status: 'publicada',
    views: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }

  const blocks = landing.pageConfig.blocks
    .filter((block) => (withVideo ? true : block.type !== 'video'))
    .map((block) => ({ ...block, id: newId() }))
  landing.pageConfig = { ...landing.pageConfig, blocks }

  const { error } = await supabase
    .from('landing_pages')
    .insert(landingRow(landing))
  if (error) {
    throw new Error(`No se ha podido crear la landing: ${error.message}`)
  }

  const { error: blocksError } = await supabase
    .from('landing_blocks')
    .insert(
      blocks.map((block, index) =>
        blockRow(block, landing.id, organizationId, index),
      ),
    )
  if (blocksError) {
    throw new Error(
      `No se han podido crear los bloques de la landing: ${blocksError.message}`,
    )
  }

  await saveConfig(organizationId, 'landing', landing.id, landingConfig(landing))
}
